import { createClient, SupabaseClient } from '@supabase/supabase-js';

export interface MigrationCredentials {
  sourceUrl: string;
  sourceKey: string;
  destinationUrl: string;
  destinationKey: string;
  tournamentId?: number | null;
}

export class MigrationService {
  private sourceClient: SupabaseClient;
  private destinationClient: SupabaseClient;
  private tournamentId?: number | null;
  private templateIdMap: Map<number, number> = new Map();

  constructor(credentials: MigrationCredentials) {
    this.sourceClient = createClient(credentials.sourceUrl, credentials.sourceKey);
    this.destinationClient = createClient(credentials.destinationUrl, credentials.destinationKey);
    this.tournamentId = credentials.tournamentId;
  }

  async migrateData(): Promise<{ success: boolean; message: string }> {
    try {
      // Step 1: Get templates from source
      const sourceTemplates = await this.getSourceTemplates();
      if (sourceTemplates.length === 0) {
        return { success: false, message: 'No templates found for the specified tournament ID or standard template' };
      }

      // Step 2: Create templates in destination
      await this.createDestinationTemplates(sourceTemplates);

      // Step 3: Get boards from source
      const sourceBoards = await this.getSourceBoards(sourceTemplates);
      if (sourceBoards.length === 0) {
        return { success: true, message: 'Templates migrated successfully, but no boards found' };
      }

      // Step 4: Create boards in destination
      await this.createDestinationBoards(sourceBoards);

      return { success: true, message: 'Migration completed successfully' };
    } catch (error: Error | any) {
      console.error('Migration error:', error);
      return { success: false, message: `Migration failed: ${error.message}` };
    }
  }

  private async getSourceTemplates() {
    let query = this.sourceClient.from('templates').select('*');

    if (this.tournamentId && Number(this.tournamentId) > 0) {
      query = query.eq('owner_id', this.tournamentId as number);
    } else {
      // Migrate standard template(s): those without owner_id
      // Supabase query builder uses .is('column', null) to check for NULL
      // @ts-ignore - query chaining types are compatible at runtime
      query = query.is('owner_id', null);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching templates: ${error.message}`);
    }

    return data || [];
  }

  private async createDestinationTemplates(sourceTemplates: any) {
    for (const template of sourceTemplates) {
      const { id, created_at, ...templateData } = template;

      const { data, error } = await this.destinationClient.from('templates').insert(templateData).select().single();

      if (error) {
        throw new Error(`Error creating template: ${error.message}`);
      }

      // Store the mapping between source and destination template IDs
      this.templateIdMap.set(id, data.id);
    }
  }

  private async getSourceBoards(sourceTemplates: any) {
    const templateIds = sourceTemplates.map((template: { id: any }) => template.id);

    const { data, error } = await this.sourceClient.from('boards').select('*').in('template_id', templateIds);

    if (error) {
      throw new Error(`Error fetching boards: ${error.message}`);
    }

    return data || [];
  }

  private async createDestinationBoards(sourceBoards: any) {
    for (const board of sourceBoards) {
      const { id, created_at, template_id, ...boardData } = board;

      // Map the source template_id to the destination template_id
      const destinationTemplateId = this.templateIdMap.get(template_id);

      if (!destinationTemplateId) {
        console.warn(`No destination template found for source template ID ${template_id}`);
        continue;
      }

      const { error } = await this.destinationClient.from('boards').insert({
        ...boardData,
        template_id: destinationTemplateId,
      });

      if (error) {
        throw new Error(`Error creating board: ${error.message}`);
      }
    }
  }
}
